import * as z from "zod";
import {
	USER_SETTINGS_COLOR_BLIND_FILTERS,
	type USER_SETTINGS_FIELDS,
	USER_SETTINGS_FONTS,
	USER_SETTINGS_ORIENTATIONS,
} from "./user-settings.constants.ts";

/**
 * @public
 */
export const ColorBlindFilterSchema = z
	.enum(USER_SETTINGS_COLOR_BLIND_FILTERS)
	.meta({ title: "ColorBlindFilter", examples: ["Deuteranopia"] });

export type ColorBlindFilter = z.infer<typeof ColorBlindFilterSchema>;

/**
 * @public
 */
export const UserSettingsFontSchema = z
	.enum(USER_SETTINGS_FONTS)
	.meta({ title: "UserSettingsFont", examples: ["Lora"] });

export type UserSettingsFont = z.infer<typeof UserSettingsFontSchema>;

/**
 * @public
 */
export const OrientationSchema = z
	.enum(USER_SETTINGS_ORIENTATIONS)
	.meta({ title: "Orientation", examples: ["landscape"] });

export type Orientation = z.infer<typeof OrientationSchema>;

/** A setting Readiant exposes as a fixed number of steps. */
const step = (max: number) => z.number().int().min(1).max(max);

/**
 * The value shape of every setting, before optionality is applied. Keys match
 * {@link USER_SETTINGS_FIELDS}.
 */
const UserSettingsValues = {
	colorBlindFilter: ColorBlindFilterSchema,
	countdownLevel: step(3),
	font: UserSettingsFontSchema,
	fontSize: step(12),
	letterSpacing: step(5),
	lineHeight: step(3),
	orientation: OrientationSchema,
	readStopLevel: step(3),
	screenModeLevel: step(3),
	subtitleLevel: step(3),
	subtitleFontSize: step(5),
	textModeLevel: step(3),
	wordSpacing: step(5),
	zoomLevel: step(5),
} as const satisfies Record<(typeof USER_SETTINGS_FIELDS)[number], z.ZodType>;

function nullish<Shape extends Record<string, z.ZodType>>(
	shape: Shape,
): { [K in keyof Shape]: z.ZodOptional<z.ZodNullable<Shape[K]>> } {
	return Object.fromEntries(
		Object.entries(shape).map(([field, schema]) => [
			field,
			schema.nullable().optional(),
		]),
	) as { [K in keyof Shape]: z.ZodOptional<z.ZodNullable<Shape[K]>> };
}

/**
 * Reading preferences of a user; only the settings they actually changed are
 * present.
 *
 * @public
 */
export const UserSettingsSchema = z
	.object(UserSettingsValues)
	.partial()
	.meta({
		title: "UserSettings",
		description: "Reading preferences of a user, unset settings omitted.",
		examples: [{ font: "Lora", fontSize: 6 }, {}],
	});

export type UserSettings = z.infer<typeof UserSettingsSchema>;

/**
 * A change to the reading preferences of a user: every setting is optional and
 * `null` clears it. At least one setting must be present, and unknown settings
 * are rejected.
 *
 * @public
 */
export const UserSettingsPatchSchema = z
	.strictObject(nullish(UserSettingsValues))
	.refine((patch) => Object.keys(patch).length > 0, {
		error: "must contain at least one field",
	})
	.meta({
		title: "UserSettingsPatch",
		description:
			"Settings to change; `null` clears a setting. At least one setting is required.",
		examples: [{ fontSize: 6 }, { font: null, zoomLevel: 3 }],
	});

export type UserSettingsPatch = z.infer<typeof UserSettingsPatchSchema>;
